import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewChild, inject } from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { TablerIconComponent } from "angular-tabler-icons";
import { BaseComponent } from "src/app/core/base/base.component";
import { MenuServiceParameterizationResponse } from "src/app/core/models/menu-service.model";
import { OrderCheckStorage } from "src/app/core/models/order-check.model";
import { CryptoService } from "src/app/core/services/crypto.service";
import { HubService } from "src/app/core/services/hub.service";
import { LoadingService } from "src/app/core/services/loading.service";
import { NotificationService } from "src/app/core/services/notification.service";
import { OrderCheckService } from "src/app/core/services/order-check.service";
import { toInt } from "src/app/core/utils/functions";
import { InputOnlyNumbersComponent } from "../../inputs/input-only-numbers/input-only-numbers.component";
import { QrCodeScannerComponent } from "../../qr-code-scanner/qr-code-scanner.component";

@Component({
  selector: "app-bind-table",
  templateUrl: "./bind-table.component.html",
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputOnlyNumbersComponent,
    TablerIconComponent,
    QrCodeScannerComponent,
  ],
})
export class BindTableBottomSheet extends BaseComponent implements OnInit {
  //Injections
  private notificationService = inject(NotificationService);
  private loadingService = inject(LoadingService);
  private orderCheckService = inject(OrderCheckService);
  private cryptoService = inject(CryptoService);
  private hubService = inject(HubService);
  private _bottomSheet = inject(MatBottomSheet);

  // Variables
  parameterization: MenuServiceParameterizationResponse =
    this.orderCheckService.getParameterization();
  orderCheck: OrderCheckStorage = this.orderCheckService.getOrderCheckStorage();
  private awaitingHubResponse = false;

  inputMode: "text" | "qr" = "text";

  @ViewChild("qrUi") qrUi?: QrCodeScannerComponent;

  // Form
  form = new FormGroup({
    BindedTableNumber: new FormControl(this.orderCheck.BindedTableNumber, [
      Validators.required,
      Validators.min(1),
    ]),
  });

  //#region Getters
  get f() {
    return this.form.controls;
  }

  ngOnInit(): void {
    this.onSubscribe();
  }

  onSubscribe() {
    this.subs.push(
      this.hubService.$menuServiceUpdateStatus.subscribe((message: string) => {
        if (!this.awaitingHubResponse) {
          return;
        }
        this.finishHubOperation();
        this.loadingService.hide();
        this.notificationService.showSuccessMessage(
          message || "Mesa alterada com sucesso",
        );
        this.orderCheckService.setOrderCheckBindedTableNumber(
          this.f.BindedTableNumber.value,
        );
        this.close();
      }),
    );

    this.subs.push(
      this.hubService.$menuServiceInternalError.subscribe(() => {
        if (!this.awaitingHubResponse) {
          return;
        }
        this.finishHubOperation();
        this.loadingService.hide();
        this.showAlert("Houve um erro ao salvar o número da mesa");
        this.resumeQrIfNeeded();
      }),
    );

    this.subs.push(
      this.hubService.$menuServiceClosedTable.subscribe(
        (description: string) => {
          if (!this.awaitingHubResponse) {
            return;
          }
          this.finishHubOperation();
          this.loadingService.hide();
          this.showAlert(description || "Mesa fechada");
          this.resumeQrIfNeeded();
        },
      ),
    );

    this.subs.push(
      this.hubService.$menuServiceInvalidTable.subscribe(
        (description: string) => {
          if (!this.awaitingHubResponse) {
            return;
          }
          this.finishHubOperation();
          this.loadingService.hide();
          this.showAlert(description || "Houve um erro o número da mesa");
          this.resumeQrIfNeeded();
        },
      ),
    );

    this.subs.push(
      this.hubService.$menuServiceBindTableTableAlreadyHasItems.subscribe(
        (description: string) => {
          if (!this.awaitingHubResponse) {
            return;
          }
          this.finishHubOperation();
          this.loadingService.hide();
          this.showAlert(
            description ||
              "Não é possível vincular uma comanda a mesa que já tenha itens",
          );
          this.resumeQrIfNeeded();
        },
      ),
    );
  }

  setInputMode(mode: "text" | "qr") {
    if (this.inputMode === "qr" && mode === "text") {
      this.qrUi?.stopCamera();
    }
    this.inputMode = mode;
  }

  close() {
    this.finishHubOperation();
    this.qrUi?.stopCamera();
    this._bottomSheet.dismiss();
  }

  private resumeQrIfNeeded() {
    if (this.inputMode === "qr") {
      this.qrUi?.restart();
    }
  }

  handleBindTable() {
    this.f["BindedTableNumber"].setValue(
      toInt(this.f["BindedTableNumber"].value?.toString() || "0"),
    );
    if (
      this.f["BindedTableNumber"].value !== this.orderCheck.BindedTableNumber
    ) {
      this.beginHubOperation();
      const table = this.orderCheckService.getHubServiceOrderCheckStatus(
        this.parameterization,
        this.orderCheck,
      );
      table.TableNumber = this.f["BindedTableNumber"].value;
      this.hubService.updateOrderCheckStatus(table);
    }
  }

  private beginHubOperation() {
    if (!this.awaitingHubResponse) {
      this.awaitingHubResponse = true;
      this.hubService.beginLocalOperation();
    }
  }

  private finishHubOperation() {
    if (this.awaitingHubResponse) {
      this.awaitingHubResponse = false;
      this.hubService.endLocalOperation();
    }
  }

  private showAlert(message: string) {
    this.notificationService.showAlertMessage(message);
  }

  onTableQrDecoded(raw: string) {
    try {
      if (/^https?:\/\//i.test(raw)) {
        this.f["BindedTableNumber"].setValue(
          toInt(this.extractLastGroupFromUrl(raw)),
        );
      } else {
        const digits = raw.replace(/\D/g, "");
        this.f["BindedTableNumber"].setValue(toInt(digits || "0"));
      }
    } catch {
      this.showAlert("QR Code inválido");
      this.qrUi?.restart();
      return;
    }
    if (
      !this.f["BindedTableNumber"].value ||
      this.f["BindedTableNumber"].invalid
    ) {
      this.showAlert("Não foi possível obter o número da mesa");
      this.qrUi?.restart();
      return;
    }
    this.handleBindTable();
  }

  extractLastGroupFromUrl(url: string) {
    const parts = url.split("/");
    let decodeBase64 = this.cryptoService.decodeBase64(parts[parts.length - 1]);
    let decryptFromAes = this.cryptoService.decryptFromAes(decodeBase64);
    return decryptFromAes;
  }

  override ngOnDestroy(): void {
    this.finishHubOperation();
    super.ngOnDestroy();
  }
}
